import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import SamplePageTile from './SamplePageTile';
import { Grid, Button } from '@gik/ui';
import { getSamplePageSituationsSearchParams, useInkindSituations } from '../../api/wp/situation';
import Link from '@/components/Link';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { translationKeys } from './i18n/en';
import { useTranslation } from 'react-i18next';
import WordpressSituation from '@gik/core/models/wordpress/WordpressSituation';

export interface ISamplePage {
  title: string;
  imageUrl: string;
}
export interface ISamplePagesGridProps extends React.HTMLAttributes<HTMLUListElement> {
  // can pass initial or mock data
  items?: ISamplePage[];
  cols?: number;
  perPage?: number;
  loadMoreButton?: boolean;
  skeleton?: boolean;
  skeletonItems?: number;
}

function getSituationVariant(item: WordpressSituation) {
  // Birth category
  if (item.parent === 29682 || item.id === 29682) {
    return 'primary';
  }
  // Medical category
  if (item.parent === 29681 || item.id === 29681) {
    return 'danger';
  }
  // Grief / Loss category
  if (item.parent === 29684 || item.id === 29684) {
    return 'success';
  }
  // General support category
  if (item.parent === 29683 || item.id === 29683) {
    return 'secondary';
  }
  return null;
}

const skelBem = bemBlock(`sample-pages-grid-skeleton`);
const Skeleton = ({ skeletonItems = 10, cols = 5 }: Partial<ISamplePagesGridProps>): React.ReactElement => (
  <Grid cols={cols} className={`${skelBem()}`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return <SamplePageTile skeleton key={index} />;
    })}
  </Grid>
);

function SamplePagesGrid({
  perPage = 5,
  cols = 5,
  loadMoreButton,
  items,
  className,
}: ISamplePagesGridProps): React.ReactElement {
  const bem = bemBlock('sample-pages-grid');
  const { t } = useTranslation();

  const { data: situations, error: situationsError } = useInkindSituations(
    1,
    100,
    { initialData: items },
    getSamplePageSituationsSearchParams(1, 100)
  );
  const [page, setPage] = React.useState<number>(1);
  const totalPages = 10;

  function handleLoadMore() {
    // TODO: this should perform backend pagination when our rest api and cache server supports it
    // for now just show all situations in one go and hide the load more button
    setPage(totalPages);
  }

  if (situations?.length === 0) {
    return <div className={bem('empty')}>{t(translationKeys.empty)}</div>;
  }

  const isLoading = !situations;
  const isError = isLoading && situationsError;

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  return (
    <div>
      <Grid cols={cols} className={bem(null, null, className)}>
        {situations.map((item, index) => {
          if (index >= page * perPage) return null;
          return (
            <Link key={index} href={item.acf.sample_inkind_page_url + '?sample-page=true'}>
              <a href={item.acf.sample_inkind_page_url + '?sample-page=true'} className={bem('item')}>
                <SamplePageTile
                  variant={getSituationVariant(item)}
                  title={item.name}
                  imageUrl={item.acf.taxonomy_svg_icon}
                />
              </a>
            </Link>
          );
        })}
      </Grid>

      {loadMoreButton && page < totalPages && (
        <Button onClick={handleLoadMore} variant="default" pill>
          Load more
        </Button>
      )}
    </div>
  );
}

export default withSuspense(SamplePagesGrid, Skeleton);
